import { AxiosResponse } from 'axios';
import { API_ROUTES } from 'data/ApiRoutes';
import { GET, PATCH, POST } from 'data/services/api';
import { buildApiPath } from 'utils';

export type TriageRecipientRole = 'to' | 'cc';

// Where a note came from. `outbound` and `inbound` are the two halves of the
// email conversation with the customer admin; `internal` is the note the
// member-notes modal has always written, which never leaves the building.
export type TriageNoteDirection = 'internal' | 'outbound' | 'inbound';

export type TriageEmailStatus = 'pending' | 'sent' | 'failed';

export interface TriageRecipient {
  userId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  role: TriageRecipientRole;
}

export interface TriageNoteAuthor {
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface TriageNoteModel {
  triagesNotesId: string;
  triagesId?: string;
  note?: string;
  createdByUserId?: string;
  createdByUser?: TriageNoteAuthor;
  createdDate: string;
  direction: TriageNoteDirection;
  emailStatus: TriageEmailStatus | null;
  emailError: string | null;
  emailFrom: string | null;
  emailMessageId: string | null;
}

export interface TriageModel {
  triagesId: string;
  associatedToId: string;
  assignedToId?: string;
  triagesStatus: string;
  /** The `triage_options` id, not a display string. */
  triagesType: string;
  /** Its display name, joined from `triage_options`. */
  triageOptionsType?: string;
  subject: string | null;
  triagesCreatedDate: string;
  /**
   * The triage row's own column is `triages_created_date`; this is only here
   * so the thread ordering does not silently collapse if the new route names
   * the field the way its notes are named.
   */
  createdDate?: string;
  recipients: TriageRecipient[];
  notes: TriageNoteModel[];
}

export interface CreateTriageRequest {
  associatedToId: string;
  triagesType: string;
  subject: string;
  toIds: string[];
  ccIds: string[];
  /** HTML from the rich text editor. Sanitized server-side before it is sent. */
  note: string;
  triagesStatus: string;
  sendEmail: boolean;
}

/** Escalation recipients configured for a company, per triage category. */
export interface CompanyEscalationRecipient {
  triageOptionsId: string;
  triageOptionsType: string;
  role: TriageRecipientRole;
  user: {
    userId: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

const rethrow = (e: unknown): never => {
  if (typeof e === 'string') {
    throw Error(e.toUpperCase());
  } else if (e instanceof Error) {
    throw Error(e.message);
  }
  throw Error('');
};

// The list route answers notes ascending, but the older getTriages query this
// one extends orders them DESC. Sorting here means "the latest outbound note"
// is the same note whichever way they arrive — that decision drives the failed
// email chip and the Retry control, so it must not depend on the server's
// ordering holding.
const ascendingByCreated = (notes?: TriageNoteModel[]): TriageNoteModel[] =>
  [...(notes || [])].sort(
    (a, b) =>
      new Date(a.createdDate).valueOf() - new Date(b.createdDate).valueOf(),
  );

const normalize = (triage: TriageModel): TriageModel => ({
  ...triage,
  recipients: triage.recipients || [],
  notes: ascendingByCreated(triage.notes),
});

export const getMemberTriages = async (
  associatedToId: string,
): Promise<TriageModel[]> => {
  try {
    const res: AxiosResponse<TriageModel[]> = await GET(
      buildApiPath({ path: API_ROUTES.triages, params: { associatedToId } }),
    );
    return (res.data || []).map(normalize);
  } catch (e: unknown) {
    return rethrow(e);
  }
};

export const createMemberTriage = async (
  body: CreateTriageRequest,
): Promise<TriageModel> => {
  try {
    const res: AxiosResponse<TriageModel> = await POST(
      API_ROUTES.triages,
      body,
    );
    return normalize(res.data);
  } catch (e: unknown) {
    return rethrow(e);
  }
};

/**
 * `readdress`, when given, REPLACES the triage's recipients server-side rather
 * than addressing this one message: inbound replies are matched against the
 * stored recipients, so mailing someone they do not list would strand the
 * answer. Omit it to reply to whoever the triage already addresses.
 */
export const replyToTriage = async (
  triagesId: string,
  note: string,
  readdress?: { toIds: string[]; ccIds: string[] },
): Promise<TriageNoteModel> => {
  try {
    const res: AxiosResponse<TriageNoteModel> = await POST(
      API_ROUTES.triageReply(triagesId),
      { note, ...readdress },
    );
    return res.data;
  } catch (e: unknown) {
    return rethrow(e);
  }
};

export const resendTriageNote = async (
  triagesId: string,
  triagesNotesId: string,
): Promise<TriageNoteModel> => {
  try {
    const res: AxiosResponse<TriageNoteModel> = await POST(
      API_ROUTES.triageNoteResend(triagesId, triagesNotesId),
      {},
    );
    return res.data;
  } catch (e: unknown) {
    return rethrow(e);
  }
};

export const updateTriageStatus = async (
  triagesId: string,
  triagesStatus: string,
): Promise<TriageModel> => {
  try {
    const res: AxiosResponse<TriageModel> = await PATCH(
      API_ROUTES.triageStatus(triagesId),
      { triagesStatus },
    );
    return normalize(res.data);
  } catch (e: unknown) {
    return rethrow(e);
  }
};
